import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil$ } from '@gh/rx/operators';
import { foldToAsciiForXeroTrackingInput, foldToAsciiForXeroTrackingInputTrimmed } from '@gh/core-util';
import { MapboxService } from './mapbox.service';
import { MapboxSuggestion, AddressComponents, MapboxRetrieve } from './types';

interface AddressAutocompleteSuggestionRow {
  suggestion: MapboxSuggestion;
  displayLabel: string;
}

@Component({
  standalone: false,
  selector: 'gh-address-autocomplete',
  template: `
    <div class="address-autocomplete">
      <div class="search-field-wrapper">
        <mat-form-field class="field full search-field" #formFieldRef>
          <mat-label>Search address</mat-label>
          <input matInput
                 #inputRef
                 [value]="inputValue"
                 [disabled]="disabled"
                 (input)="onInput($event)"
                 (focus)="onFocus()"
                 (blur)="onBlur()"
                 (keydown)="onKeyDown($event)">
          <button *ngIf="inputValue" 
                  mat-icon-button 
                  type="button"
                  (click)="onClearInput()"
                  class="clear-button"
                  aria-label="Clear">
            <mat-icon svgIcon="gh:close" class="s16"></mat-icon>
          </button>
        </mat-form-field>
        <div *ngIf="isLoading" class="loading-bar" role="progressbar" aria-label="Loading"></div>
      </div>

      <div (click)="onShowManualFields()" class="manual-entry-link" *ngIf="!showManualFields && !disabled" role="button" tabindex="0" (keydown.enter)="onShowManualFields()" (keydown.space)="$event.preventDefault(); onShowManualFields()">
        <a class="manual-link" (click)="$event.preventDefault(); onShowManualFields()" tabindex="-1">Enter address manually</a>
      </div>

      <div #suggestionsRef 
           class="address-suggestions" 
           *ngIf="showSuggestions && !disabled"
           [style.top.px]="dropdownTop"
           [style.left.px]="dropdownLeft"
           [style.width.px]="dropdownWidth">
        <div class="suggestion-item"
             *ngFor="let row of suggestionRows; let i = index"
             (click)="onSuggestionSelect(row.suggestion)"
             (mouseenter)="hoveredIndex = i"
             [class.hovered]="hoveredIndex === i">
          <span>{{ row.displayLabel }}</span>
        </div>
        <div class="no-results" *ngIf="!isLoading && suggestionRows?.length === 0 && inputValue.length >= 2">
          <div class="no-results-message">No address found for "{{ inputValue }}".</div>
          <div class="no-results-action">
            <span>Try another search or </span>
            <a (click)="onShowManualFields()" class="enter-manually-link">enter manually</a>
          </div>
        </div>
        <div class="manual-entry-button-wrapper" *ngIf="suggestionRows?.length > 0">
          <button class="manual-entry-button" (click)="onShowManualFields()" type="button">Can't find the address? Enter address manually</button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .address-autocomplete { position: relative; width: 100%; }
    .search-field-wrapper { position: relative; width: 100%; }
    .search-field { width: 100%; position: relative; }
    .search-field ::ng-deep .mat-form-field-flex,
    .search-field ::ng-deep .mat-mdc-form-field-flex { align-items: center; }
    .search-field ::ng-deep .mat-form-field-infix,
    .search-field ::ng-deep .mat-mdc-form-field-infix { 
      display: flex; 
      align-items: center; 
      width: 100%; 
    }
    .search-field ::ng-deep .mat-form-field-infix input,
    .search-field ::ng-deep .mat-mdc-form-field-infix input { 
      flex: 1; 
      min-width: 0; 
      padding-right: 40px !important;
    }
    .search-field ::ng-deep .mat-mdc-form-field-should-float .mdc-text-field__input,
    .search-field ::ng-deep .mat-mdc-form-field-focused .mdc-text-field__input,
    .search-field ::ng-deep .mat-mdc-form-field-has-value .mdc-text-field__input,
    .search-field ::ng-deep .mat-mdc-form-field-should-float .mat-mdc-form-field-infix input,
    .search-field ::ng-deep .mat-mdc-form-field-focused .mat-mdc-form-field-infix input,
    .search-field ::ng-deep .mat-mdc-form-field-has-value .mat-mdc-form-field-infix input,
    .search-field ::ng-deep .mat-mdc-form-field-infix[class*=ng-tns] .mdc-floating-label--float-above ~ .mdc-text-field__input,
    .search-field ::ng-deep .mat-mdc-form-field-infix[class*=ng-tns] .mdc-floating-label--float-above ~ input.mdc-text-field__input {
      padding-top: 22px !important;
      padding-bottom: 4px !important;
      margin-top: 0 !important;
    }
    .search-field ::ng-deep .mat-mdc-form-field-should-float .mat-mdc-form-field-infix,
    .search-field ::ng-deep .mat-mdc-form-field-focused .mat-mdc-form-field-infix,
    .search-field ::ng-deep .mat-mdc-form-field-has-value .mat-mdc-form-field-infix {
      padding-top: 0 !important;
      padding-bottom: 4px !important;
      margin-top: 0 !important;
      min-height: 40px !important;
      height: 40px !important;
      max-height: 40px !important;
    }
    .search-field ::ng-deep .mat-form-field-suffix,
    .search-field ::ng-deep .mat-mdc-form-field-suffix { display: none; }
    .search-field ::ng-deep .mat-form-field-subscript-wrapper,
    .search-field ::ng-deep .mat-mdc-form-field-subscript-wrapper { position: relative; }
    // When label is NOT floating, use 12px padding-top for address search field
    // Use higher specificity to override global styles
    .search-field ::ng-deep .mat-mdc-form-field:not(.mat-mdc-form-field-should-float):not(.mat-mdc-form-field-focused):not(.mat-mdc-form-field-has-value) .mat-mdc-form-field-infix {
      padding-top: 22px !important;
      padding-bottom: 4px !important;
      min-height: 40px !important;
      height: 40px !important;
      max-height: 40px !important;
    }
    .search-field .clear-button { 
      position: absolute !important; 
      right: 8px !important; 
      bottom: 0px !important;
      top: auto !important; 
      left: auto !important;
      transform: none !important;
      width: 24px !important; 
      height: 24px !important; 
      min-width: 24px !important;
      padding: 0 !important;
      margin: 0 !important;
      z-index: 2;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
    .search-field .clear-button ::ng-deep .mat-mdc-button-persistent-ripple,
    .search-field .clear-button ::ng-deep .mat-mdc-button-ripple,
    .search-field .clear-button ::ng-deep .mat-mdc-focus-indicator,
    .search-field .clear-button ::ng-deep .mat-mdc-button-touch-target {
      display: none !important;
    }
    .search-field .clear-button mat-icon { 
      color: rgba(0, 0, 0, 0.54) !important; 
      font-size: 16px !important; 
      width: 16px !important; 
      height: 16px !important; 
      margin: 0 !important;
    }
    .search-field .clear-button:hover mat-icon { color: rgba(0, 0, 0, 0.87); }
    .address-suggestions { position: fixed; background: white; border: 1px solid #ccc; border-radius: 4px; max-height: 200px; overflow-y: auto; z-index: 9999; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); min-width: 280px; }
    .suggestion-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid #eee; font-size: 14px; line-height: 1.4; }
    .suggestion-item:last-child { border-bottom: none; }
    .suggestion-item:hover, .suggestion-item.hovered { background-color: #f5f5f5; }
    .no-results { padding: 16px; text-align: center; background-color: #fafafa; }
    .no-results-message { color: #666; font-size: 14px; font-style: italic; margin-bottom: 12px; }
    .no-results-action { font-size: 13px; color: #666; }
    .enter-manually-link { color: #1976d2; cursor: pointer; text-decoration: none; }
    .enter-manually-link:hover { color: #1565c0; text-decoration: underline; }
    .loading-bar {
      position: absolute !important;
      bottom: 24px;
      left: 0;
      right: 8px;
      height: 4px;
      z-index: 2;
      border-radius: 0;
      overflow: hidden;
      background: transparent;
    }
    .loading-bar::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 40%;
      background: #1976d2;
      animation: address-loader-slide 1.5s ease-in-out infinite;
    }
    @keyframes address-loader-slide {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(350%); }
    }
    .manual-entry-link { position: relative; z-index: 10; max-width: 140px; margin-top: -24px; padding: 4px 0; cursor: pointer; animation: fadeInDown 0.3s ease-out; pointer-events: auto; }
    .manual-link { color: #1976d2; text-decoration: none; font-size: 13px; cursor: pointer; font-weight: 600; transition: color 0.2s ease; display: inline-block; margin-top: -8px; pointer-events: auto; }
    .manual-link:hover { color: #1565c0; text-decoration: none; }
    .manual-entry-button-wrapper { border-top: 1px solid #eee; padding: 8px; background-color: #fafafa; animation: fadeInUp 0.3s ease-out; }
    .manual-entry-button { width: 100%; padding: 8px 12px; background-color: transparent; border: 1px solid #ccc; border-radius: 4px; cursor: pointer; font-size: 13px; color: #1976d2; text-align: center; transition: all 0.2s ease; }
    .manual-entry-button:hover { background-color: #f5f5f5; border-color: #1976d2; transform: translateY(-1px); box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
    @keyframes fadeInDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    ::ng-deep .manual-address-fields-wrapper {
      animation: slideDownFadeIn 0.4s ease-out;
      overflow: hidden;
    }
    @keyframes slideDownFadeIn {
      from {
        opacity: 0;
        max-height: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        max-height: 1000px;
        transform: translateY(0);
      }
    }
    `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressAutocompleteComponent),
      multi: true
    }
  ]
})
export class AddressAutocompleteComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() includeFields: (keyof AddressComponents)[] = ['streetNo', 'street', 'city', 'state', 'postalCode', 'country'];
  @Input() showManualFields = false;
  @Input() disabled = false;
  @Input() foldForXeroTracking = false;
  @Output() addressSelected = new EventEmitter<AddressComponents>();
  @Output() showManualFieldsChange = new EventEmitter<boolean>();

  @ViewChild('inputRef') inputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('suggestionsRef') suggestionsRef!: ElementRef<HTMLDivElement>;
  @ViewChild('formFieldRef', { read: ElementRef }) formFieldRef!: ElementRef<HTMLElement>;

  suggestionRows: AddressAutocompleteSuggestionRow[] = [];
  showSuggestions = false;
  inputValue = '';
  hoveredIndex = -1;
  isLoading = false;
  isUserTyping = false;
  dropdownTop = 0;
  dropdownLeft = 0;
  dropdownWidth = 280;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private readonly onWindowResizeOrScroll = (): void => {
    this.handleWindowResize();
  };

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  suggestions$ = this.mapboxService.createSearchStream(this.searchSubject);

  constructor(private mapboxService: MapboxService) {}

  ngOnInit(): void {
    this.subscribeToSuggestions();
    this.addWindowResizeListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.removeWindowResizeListener();
  }

  writeValue(value: string): void {
    this.inputValue = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    if (this.disabled) return;
    const el = event.target as HTMLInputElement;
    const raw = el.value;
    const next = this.foldForXeroTracking ? foldToAsciiForXeroTrackingInput(raw) : raw;
    if (raw !== next) {
      el.value = next;
      const start = el.selectionStart ?? raw.length;
      const end = el.selectionEnd ?? raw.length;
      const caretAtEnd = start === raw.length && end === raw.length;
      if (caretAtEnd) {
        el.setSelectionRange(next.length, next.length);
      } else {
        const delta = next.length - raw.length;
        el.setSelectionRange(
          Math.max(0, Math.min(next.length, start + delta)),
          Math.max(0, Math.min(next.length, end + delta)),
        );
      }
    }
    this.inputValue = next;
    this.onChange(next);
    this.isUserTyping = true;

    if (next.length >= 2) {
      this.isLoading = true;
      this.searchSubject.next(next);
    } else {
      this.clearSuggestions();
    }
  }

  onFocus(): void {
    if (this.disabled) return;
    if (this.suggestionRows.length > 0) {
      this.showSuggestions = true;
      setTimeout(() => this.updateDropdownPosition(), 0);
    }
  }

  onBlur(): void {
    setTimeout(() => {
      if (!this.showSuggestions) {
        this.isUserTyping = false;
      }
    }, 150);
  }

  onSuggestionSelect(suggestion: MapboxSuggestion): void {
    this.isLoading = true;
    this.fetchSuggestionDetails(suggestion.mapbox_id);
    this.showSuggestions = false;
    this.isUserTyping = false;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.showSuggestions) {
      return;
    }
    
    if (!this.suggestionRows.length) {
      if (event.key === 'Escape') {
        this.showSuggestions = false;
      }
      return;
    }
  
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.hoveredIndex = (this.hoveredIndex + 1) % this.suggestionRows.length;
        this.scrollToHovered();
        break;
  
      case 'ArrowUp':
        event.preventDefault();
        this.hoveredIndex = (this.hoveredIndex - 1 + this.suggestionRows.length) % this.suggestionRows.length;
        this.scrollToHovered();
        break;
  
      case 'Enter':
        if (this.hoveredIndex >= 0 && this.hoveredIndex < this.suggestionRows.length) {
          this.onSuggestionSelect(this.suggestionRows[this.hoveredIndex].suggestion);
          event.preventDefault();
        }
        break;
  
      case 'Escape':
        this.showSuggestions = false;
        break;
    }
  }

  private updateDropdownPosition(): void {
    let elementRect: DOMRect;
    let element: HTMLElement | null = null;
    
    if (this.formFieldRef?.nativeElement) {
      element = this.formFieldRef.nativeElement;
    } else if (this.inputRef?.nativeElement) {
      const formField = this.inputRef.nativeElement.closest('mat-form-field');
      if (formField) {
        element = formField as HTMLElement;
      } else {
        element = this.inputRef.nativeElement;
      }
    }
    
    if (element) {
      elementRect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const minDropdownWidth = 280;
      const gapBelowField = -20;

      let top = elementRect.bottom + gapBelowField;
      let left = elementRect.left;
      let width = Math.max(elementRect.width, minDropdownWidth);

      if (top + 200 > viewportHeight) {
        top = elementRect.top - 200;
      }

      if (left + width > viewportWidth) {
        left = viewportWidth - width - 10;
      }

      if (left < 0) {
        left = 10;
        width = Math.min(width, viewportWidth - 20);
      }

      if (width < minDropdownWidth) {
        width = Math.min(minDropdownWidth, viewportWidth - 20);
        if (left + width > viewportWidth) {
          left = viewportWidth - width - 10;
        }
      }
      
      this.dropdownTop = top;
      this.dropdownLeft = left;
      this.dropdownWidth = width;
    }
  }

  private addWindowResizeListener(): void {
    window.addEventListener('resize', this.onWindowResizeOrScroll);
    window.addEventListener('scroll', this.onWindowResizeOrScroll);
  }

  private removeWindowResizeListener(): void {
    window.removeEventListener('resize', this.onWindowResizeOrScroll);
    window.removeEventListener('scroll', this.onWindowResizeOrScroll);
  }

  private handleWindowResize(): void {
    if (this.showSuggestions) {
      this.updateDropdownPosition();
    }
  }

  private scrollToHovered(): void {
    if (this.suggestionsRef && this.suggestionsRef.nativeElement) {
      const list = this.suggestionsRef.nativeElement.children;
      const hoveredElement = list[this.hoveredIndex] as HTMLElement;
  
      if (hoveredElement) {
        hoveredElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }

  private subscribeToSuggestions(): void {
    this.suggestions$
      .pipe(takeUntil$(this.destroy$))
      .subscribe({
        next: suggestions => this.handleSuggestions(suggestions),
        error: () => this.isLoading = false,
      });
  }

  private handleSuggestions(suggestions: MapboxSuggestion[]): void {
    this.suggestionRows = suggestions.map((s) => ({
      suggestion: s,
      displayLabel: this.buildSuggestionDisplayLabel(s),
    }));
    this.showSuggestions = this.inputValue.length >= 2 && this.isUserTyping;
    if (this.showSuggestions) {
      setTimeout(() => this.updateDropdownPosition(), 0);
    }
    this.isLoading = false;
  }

  private fetchSuggestionDetails(mapboxId: string): void {
    this.mapboxService.retrieveSuggestion(mapboxId).subscribe({
      next: result => this.processSuggestion(result),
      error: () => this.isLoading = false,
    });
  }

  private processSuggestion(result: MapboxRetrieve | null): void {
    if (result) {
      const components = this.mapboxService.getAddressComponents(result);
      const fullAddress = this.buildFullAddress(components);
      this.addressSelected.emit(components);
      this.inputValue = fullAddress;
      this.clearSuggestions();
      this.mapboxService.resetSessionToken();
      if (!this.showManualFields) {
        this.showManualFields = true;
        this.showManualFieldsChange.emit(true);
      }
    }
    this.isLoading = false;
  }

  onShowManualFields(): void {
    if (this.disabled) return;
    this.showManualFields = true;
    this.showManualFieldsChange.emit(true);
    this.showSuggestions = false;
  }

  onClearInput(): void {
    this.inputValue = '';
    this.onChange('');
    this.clearSuggestions();
    if (this.inputRef?.nativeElement) {
      this.inputRef.nativeElement.focus();
    }
  }

  private buildFullAddress(components: AddressComponents): string {
    const parts: string[] = [];

    if (this.includeFields.includes('streetNo') || this.includeFields.includes('street')) {
      const streetParts: string[] = [];
      if (this.includeFields.includes('streetNo') && components.streetNo?.trim()) {
        streetParts.push(components.streetNo.trim());
      }
      if (this.includeFields.includes('street') && components.street?.trim()) {
        streetParts.push(components.street.trim());
      }
      if (streetParts.length > 0) {
        parts.push(streetParts.join(' '));
      }
    }

    if (this.includeFields.includes('city') && components.city?.trim()) {
      parts.push(components.city.trim());
    }

    if (this.includeFields.includes('state') || this.includeFields.includes('postalCode')) {
      const statePostalParts: string[] = [];
      if (this.includeFields.includes('state') && components.state?.trim()) {
        statePostalParts.push(components.state.trim());
      }
      if (this.includeFields.includes('postalCode') && components.postalCode?.trim()) {
        statePostalParts.push(components.postalCode.trim());
      }
      if (statePostalParts.length > 0) {
        parts.push(statePostalParts.join(' '));
      }
    }

    if (this.includeFields.includes('country') && components.country?.trim()) {
      parts.push(components.country.trim());
    }

    const joined = parts.join(', ');
    return this.foldForXeroTracking ? foldToAsciiForXeroTrackingInputTrimmed(joined) : joined;
  }

  private buildSuggestionDisplayLabel(suggestion: MapboxSuggestion): string {
    const full = suggestion.full_address?.trim();
    if (full) {
      return this.foldForXeroTracking ? foldToAsciiForXeroTrackingInputTrimmed(full) : full;
    }
    const name = suggestion.name ?? '';
    const place = suggestion.place_formatted ?? '';
    if (name && place) {
      const combined = `${name}, ${place}`;
      return this.foldForXeroTracking ? foldToAsciiForXeroTrackingInputTrimmed(combined) : combined;
    }
    const fallback = name || place;
    return this.foldForXeroTracking ? foldToAsciiForXeroTrackingInputTrimmed(fallback) : fallback;
  }

  private clearSuggestions(): void {
    this.suggestionRows = [];
    this.showSuggestions = false;
    this.isLoading = false;
    this.isUserTyping = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInsideInput = this.inputRef?.nativeElement.contains(target);
    const clickedInsideSuggestions = this.suggestionsRef?.nativeElement.contains(target);

    if (!clickedInsideInput && !clickedInsideSuggestions) {
      this.showSuggestions = false;
      this.isUserTyping = false;
      this.onTouched();
    }
  }
}
